import { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { Loader, type ValidationErrors } from 'sonata-react-components';
import { CampaignFamilyEnum } from 'sonata-static-storage/lib/schemas/campaign/campaign.schema';
import { GeoDataAndScreensInteface } from 'sonata-static-storage/lib/schemas/screen/screen.schema';
import { Zone } from 'sonata-static-storage/lib/schemas/zone/zone.schema';

import { getCommittedAttributionType } from '@/components/common/MultiFlightMap/MultiFlightMap.helpers';
import { getSidebarConfig, Sidebar } from '@/components/common/Sidebar';
import {
  checkAdCreativesAreConfigured,
  checkContextTargetingAreConfigured,
  checkLocationsOrScreensAreConfigured,
  checkMainSettingsAreConfigured
} from '@/components/common/Sidebar/mainSection.helper';
import InsertionOrderForm from '@/components/ioSettings/InsertionOrderForm/InsertionOrderForm';
import { useInsertionOrderFormContext } from '@/components/ioSettings/InsertionOrderForm/InsertionOrderForm.provider';
import LineItemForm from '@/components/liSettings/LineItemForm/LineItemForm';
import { useLineItemFormContext } from '@/components/liSettings/LineItemForm/LineItemForm.provider';
import { useGlobalStore } from '@/store/global';
import { getUser } from '@/store/global/user/selectors';
import { getUserRoles } from '@/store/global/userRoles/selectors.ts';
import { IoSettingsStore, useStore as useIOSettingsStore } from '@/store/local/ioSettings';
import { LineItemStore, useStore as useLineItemStore } from '@/store/local/lineItem';
import { FloatingAudiencePanel } from './FloatingAudiencePanel/FloatingAudiencePanel';
import { MainSectionStyled } from './MainSection.styled';

import type { MultiFlightEntityType } from 'sonata-static-storage/lib/common/common.types';
import type { ListType } from '@/components/common/MultiFlightMap/multiFlightMap.types';
import type { FormSection, FormSubsection } from '@/types/formSections.types';

type SectionRef = {
  scrollToSection: (sectionId: string) => void;
};

type Props = {
  isEditMode: boolean;
  origin: MultiFlightEntityType;
};

type ContentProps = {
  activeSubsection: string | null;
  entityHasMediaChannel: boolean;
  entitySettings: IoSettingsStore | LineItemStore;
  entityType: MultiFlightEntityType;
  getDraftValidationErrors?: () => ValidationErrors | undefined;
  getFormErrors: () => ValidationErrors;
  getScreens: () => GeoDataAndScreensInteface[] | null;
  getZones: () => Zone[] | null;
  isEditMode: boolean;
  isSnliActive: boolean;
  list: ListType;
  origin: MultiFlightEntityType;
  selectedSection: FormSection | null;
  setActiveSubsection: (subsection: FormSubsection | null) => void;
  setList: (list: ListType) => void;
  setSelectedSection: (section: FormSection | null) => void;
  shouldShowValidationErrors: () => boolean;
};

function Content({
  activeSubsection,
  entityHasMediaChannel,
  entitySettings,
  getDraftValidationErrors,
  getFormErrors,
  getScreens,
  getZones,
  isEditMode,
  isSnliActive,
  list,
  origin,
  selectedSection,
  setActiveSubsection,
  setList,
  setSelectedSection,
  shouldShowValidationErrors
}: ContentProps) {
  const { formatMessage } = useIntl();
  const user = useGlobalStore(getUser);
  const sectionRef = useRef<SectionRef>(null);
  const userJSON = user?.toJSON();
  const userRoles = useGlobalStore(getUserRoles);
  const errors = getFormErrors();

  useEffect(() => {
    if (entityHasMediaChannel) {
      setSelectedSection('mainSettings');
    }
  }, [entityHasMediaChannel]);

  const areLocationsOrScreensConfigured = checkLocationsOrScreensAreConfigured(
    entitySettings.type === CampaignFamilyEnum.dooh ? getScreens() : getZones()
  );

  const isDooh = entitySettings.type === CampaignFamilyEnum.dooh;
  const isDigital = entitySettings.type === CampaignFamilyEnum.standard;
  const mapEntities = entitySettings.type === undefined ? [] : (isDooh ? getScreens() : getZones()) || [];

  const isLocationsSectionEnabled = entityHasMediaChannel;
  const isLocationsSectionChecked = isLocationsSectionEnabled && areLocationsOrScreensConfigured;
  const isMainSettingsSectionChecked = entityHasMediaChannel && checkMainSettingsAreConfigured(errors);
  const isContentTargetingEnabled = entityHasMediaChannel;
  const isContextTargetingChecked = isContentTargetingEnabled && checkContextTargetingAreConfigured(errors);
  const isCreativesEnabled = entityHasMediaChannel && (!isDooh || mapEntities?.length > 0);
  const isCreativesChecked =
    isCreativesEnabled && checkAdCreativesAreConfigured(errors) && entitySettings.ads.length > 0;
  const showLoader = !mapEntities;

  const handleSubsectionListChange = (subsection: FormSubsection) => {
    let newList: ListType | null = null;
    switch (subsection) {
      case 'activationAreas':
        newList = 'locations';
        break;
      case 'screensSubsection':
        newList = 'screens';
        break;
      case 'venues':
      case 'attributionPOIs':
      case 'proximityPoints':
      default:
        newList = 'venues';
        break;
    }
    if (['attributionPOIs', 'proximityPoints'].includes(subsection)) {
      setActiveSubsection(subsection);
    }
    if (newList) {
      setList(newList);
    }
  };

  const handleSubsectionClick = (subsection: FormSubsection) => () => {
    if (
      subsection === 'attributionPOIs' ||
      subsection === 'proximityPoints' ||
      subsection === 'activationAreas' ||
      subsection === 'screensSubsection' ||
      subsection === 'venues'
    ) {
      handleSubsectionListChange(subsection);
    }
    sectionRef.current?.scrollToSection(subsection);
  };

  const handleSectionClick = (newSection: FormSection) => () => {
    if (selectedSection !== newSection) {
      setSelectedSection(newSection);

      let subsectionIdToHighlight: FormSubsection;
      switch (newSection) {
        case 'mainSettings':
          subsectionIdToHighlight = origin === 'insertionOrder' ? 'mediaChannel' : 'ioSettings';
          break;
        case 'contextTargeting':
          subsectionIdToHighlight =
            entitySettings.type !== CampaignFamilyEnum.dooh ? 'locationQualityIndexes' : 'appMarketplace';
          break;
        case 'creatives':
          subsectionIdToHighlight = 'adCreatives';
          break;
        default:
          return;
      }
      handleSubsectionClick(subsectionIdToHighlight)();
    }
  };

  const attributionType = getCommittedAttributionType(entitySettings.venues);

  const sidebarConfig = getSidebarConfig({
    activeSubsection,
    areLocationsOrScreensConfigured,
    attributionType,
    contextualBidding: entitySettings.contextualBidding,
    dco: entitySettings.dco,
    doohAudience: entitySettings.doohAudience,
    draftErrors: getDraftValidationErrors?.(),
    entityTargeting: entitySettings.targeting,
    entityType: origin,
    errors,
    formatMessage,
    handleSectionClick,
    handleSubsectionClick,
    isContentTargetingEnabled,
    isContextTargetingChecked,
    isCreativesChecked,
    isCreativesEnabled,
    isDigital,
    isDooh,
    isLocationsSectionChecked,
    isLocationsSectionEnabled,
    isMainSettingsSectionChecked,
    list,
    pixels: entitySettings.pixels,
    sectionRef,
    setActiveSubsection,
    setSelectedSection,
    shouldShowValidationErrors: shouldShowValidationErrors(),
    type: entitySettings.type,
    userJSON,
    userRoles
  });

  return (
    <MainSectionStyled className="ioMainSection" selectedSection={selectedSection}>
      {showLoader && <Loader showBackground type="landing" variant="default" />}
      {!isSnliActive && (
        <Sidebar<FormSection> active={selectedSection} className="mainSection__sidebar" config={sidebarConfig} />
      )}
      {origin === 'insertionOrder' ? (
        <InsertionOrderForm className="mainSection__form" isEditMode={isEditMode} ref={sectionRef} />
      ) : (
        <LineItemForm className="mainSection__form" isEditMode={isEditMode} ref={sectionRef} />
      )}
      {!isSnliActive && (
        <FloatingAudiencePanel
          country={entitySettings.countries[0]}
          targeting={entitySettings.targeting}
          zones={getZones()}
        />
      )}
    </MainSectionStyled>
  );
}

export function IoMainSection({ isEditMode, origin }: { isEditMode: boolean; origin: MultiFlightEntityType }) {
  const {
    activeSubsection,
    getFormErrors,
    getScreens,
    getZones,
    isSnliActive,
    list,
    selectedSection,
    setActiveSubsection,
    setList,
    setSelectedSection,
    shouldShowValidationErrors
  } = useInsertionOrderFormContext();

  const ioSettings = useIOSettingsStore() as IoSettingsStore;
  const ioHasMediaChannel = Boolean(ioSettings.type);

  return (
    <Content
      activeSubsection={activeSubsection}
      entityHasMediaChannel={ioHasMediaChannel}
      entitySettings={ioSettings}
      entityType="insertionOrder"
      getFormErrors={getFormErrors}
      getScreens={getScreens}
      getZones={getZones}
      isEditMode={isEditMode}
      isSnliActive={isSnliActive}
      list={list}
      origin={origin}
      selectedSection={selectedSection}
      setActiveSubsection={setActiveSubsection}
      setList={setList}
      setSelectedSection={setSelectedSection}
      shouldShowValidationErrors={shouldShowValidationErrors}
    />
  );
}

export function LiMainSection({ isEditMode, origin }: { isEditMode: boolean; origin: MultiFlightEntityType }) {
  const {
    activeSubsection,
    getDraftValidationErrors,
    getFormErrors,
    getScreens,
    getZones,
    isSnliActive,
    list,
    selectedSection,
    setActiveSubsection,
    setList,
    setSelectedSection,
    shouldShowValidationErrors
  } = useLineItemFormContext();

  const liSettings = useLineItemStore() as LineItemStore;
  const liHasMediaChannel = Boolean(liSettings.type);

  return (
    <Content
      activeSubsection={activeSubsection}
      entityHasMediaChannel={liHasMediaChannel}
      entitySettings={liSettings}
      entityType="lineItem"
      getDraftValidationErrors={getDraftValidationErrors}
      getFormErrors={getFormErrors}
      getScreens={getScreens}
      getZones={getZones}
      isEditMode={isEditMode}
      isSnliActive={isSnliActive}
      list={list}
      origin={origin}
      selectedSection={selectedSection}
      setActiveSubsection={setActiveSubsection}
      setList={setList}
      setSelectedSection={setSelectedSection}
      shouldShowValidationErrors={shouldShowValidationErrors}
    />
  );
}

export default function MainSection({ isEditMode, origin }: Readonly<Props>) {
  return origin === 'insertionOrder' ? (
    <IoMainSection isEditMode={isEditMode} origin={origin} />
  ) : (
    <LiMainSection isEditMode={isEditMode} origin={origin} />
  );
}
